import { injectable } from 'inversify';
import { NeoModel, Misc, Security, NotifyUtils } from '@singularsystems/neo-core';

import AuthorisationTypes from '../AuthorisationTypes';
import { UserGroupRolesLookup } from '../Models/UserRoleLookup';
import { observable } from 'mobx';
import MembershipChange from '../Models/Hubs/MembershipChange';
import AssignedRolesChange from '../Models/Hubs/AssignedRolesChange';

const rolesStorageKey = "neo.roles";

@injectable()
@NeoModel
export default class AuthorisationService implements Security.IAuthorisationService {

    @observable.ref
    private roleDictionary: { [index: string]: boolean } = {};

    private userGroupRolesLookup: UserGroupRolesLookup[] = [];

    constructor(
        private apiClient = Misc.Globals.appService.get(AuthorisationTypes.ApiClients.AuthorisationApiClient),
        private rolesHub = Misc.Globals.appService.get(AuthorisationTypes.ApiClients.UserRolesHub)) {

        // If the user refreshes their browser, get the roles from storage.
        // Session storage is cleared when the browser is closed.
        const roleJson = sessionStorage.getItem(rolesStorageKey);
        if (roleJson) {
            this.userGroupRolesLookup = JSON.parse(roleJson);
            this.flattenRoles(false);
        }

        // this.rolesHub.connectIfAuthenticated();

        // this.rolesHub.membershipChanged.subscribe(this.onMembershipChanged.bind(this));
        // this.rolesHub.assignedRoleChanged.subscribe(this.onAssignedRolesChanged.bind(this));
        // this.rolesHub.userGroupRemoved.subscribe(this.onUserGroupRemoved.bind(this));
    }

    public async loadRoles(showChangeNotification = false) {
        // fetch the rules
        try {
            this.userGroupRolesLookup = (await this.apiClient.users.getCurrentUserRoles()).data;

            this.flattenRoles(showChangeNotification);

            //this.rolesHub.connect();

        } catch (e) {
            this.roleDictionary = {};
            throw e;
        }
    }

    private onMembershipChanged(membershipChange: MembershipChange) {
        if (membershipChange.isNew) {
            this.loadRoles(true);
            this.rolesHub.subscribeToGroup(membershipChange.userGroupId);
        } else {
            this.onUserGroupRemoved(membershipChange.userGroupId);
        }
    }

    private onUserGroupRemoved(userGroupId: number) {
        const index = this.userGroupRolesLookup.findIndex(ug => ug.userGroupId === userGroupId);
        if (index >= 0) {
            this.userGroupRolesLookup.splice(index, 1);
            this.flattenRoles(true);
        }
    }

    private onAssignedRolesChanged(rolesChange: AssignedRolesChange) {
        const userGroup = this.userGroupRolesLookup.find(ug => ug.userGroupId === rolesChange.userGroupId);
        if (userGroup) {
            for (const roleId of rolesChange.removedRoles) {
                const index = userGroup.roles.findIndex(r => r.roleId === roleId);
                if (index >= 0) {
                    userGroup.roles.splice(index, 1);
                }
            }
            userGroup.roles.push(...rolesChange.addedRoles);
            this.flattenRoles(true);
        }
    }

    private showChangeNotification() {
        NotifyUtils.addWarning("Access", "Your access rights have changed.", 4);
    }

    private flattenRoles(showChangeNotification: boolean) {
        const roleDictionary: { [index: string]: boolean } = {};

        sessionStorage.setItem(rolesStorageKey, JSON.stringify(this.userGroupRolesLookup));

        for (const group of this.userGroupRolesLookup) {
            for (const role of group.roles) {
                roleDictionary[role.roleKey] = true;
            }
        }

        this.roleDictionary = roleDictionary;

        if (showChangeNotification) {
            this.showChangeNotification();
        }
    }

    public hasRole(roleName: string) {
        return this.roleDictionary[roleName] !== undefined;
    }
}